import { toast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { supabase } from "../../../supabase/supabase";

interface InlineAssistantNameProps {
  assistantId: string;
  name: string;
  onRenamed: (name: string) => void;
  className?: string;
  // "title": the page heading; a single click renames. "row": a list card, where a
  // click selects the card; clicking the name of the already-selected card renames
  // (Finder-style), so the editor that opens on select can't steal focus mid-rename.
  variant?: "row" | "title";
  selected?: boolean;
}

// Click-to-edit text (Linear/Notion style): reads as plain text, hover tints it, and
// editing swaps in an input with the same type so nothing shifts. Enter or blur saves,
// Esc cancels. Writes assistants.name only. Clicks and keys stop here so a clickable
// parent (the list card) does not react.
export default function InlineAssistantName({
  assistantId,
  name,
  onRenamed,
  className = "",
  variant = "row",
  selected = false,
}: InlineAssistantNameProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [isSaving, setIsSaving] = useState(false);
  // Set once Enter, Esc or blur has handled this edit: disabling or unmounting the
  // focused input fires blur again, which must not save twice (or save after Esc).
  const settled = useRef(false);
  const isTitle = variant === "title";
  // Shared by the text and the input so they occupy the same box.
  const box = `rounded-md ${isTitle ? "-mx-2 px-2" : "-mx-1 px-1"}`;

  const startEditing = () => {
    settled.current = false;
    setDraft(name);
    setIsEditing(true);
  };

  const cancel = () => {
    settled.current = true;
    setIsEditing(false);
  };

  const save = async () => {
    if (settled.current) return;
    settled.current = true;
    const next = draft.trim();
    if (next === name) {
      setIsEditing(false);
      return;
    }
    if (!next) {
      toast({ title: "Rename Failed", description: "Assistant name is required", variant: "destructive" });
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    const { error } = await supabase
      .from("assistants")
      .update({ name: next, updated_at: new Date().toISOString() })
      .eq("id", assistantId);
    setIsSaving(false);
    setIsEditing(false);

    if (error) {
      toast({ title: "Rename Failed", description: error.message || "Failed to rename assistant", variant: "destructive" });
      return;
    }
    onRenamed(next);
    toast({ title: "Assistant Renamed", description: `Renamed to "${next}".` });
  };

  if (isEditing || isSaving) {
    return (
      <span
        className="flex min-w-0 max-w-full items-center gap-2"
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      >
        {/* The invisible copy sizes the grid cell to the text, so the input is exactly as wide as the name. */}
        <span className={`inline-grid min-w-0 max-w-full ${className}`}>
          <span aria-hidden className={`invisible col-start-1 row-start-1 overflow-hidden whitespace-pre ${box}`}>
            {draft || " "}
          </span>
          <input
            autoFocus
            value={draft}
            disabled={isSaving}
            aria-label="Assistant name"
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                save();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancel();
              }
            }}
            className={`col-start-1 row-start-1 w-full min-w-[4ch] bg-secondary/60 text-foreground outline-none ring-1 ring-border disabled:opacity-60 ${box}`}
          />
        </span>
        {isSaving && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
      </span>
    );
  }

  if (isTitle) {
    return (
      <button
        type="button"
        title={name}
        onClick={startEditing}
        className={`min-w-0 max-w-full cursor-text truncate text-left transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${box} ${className}`}
      >
        {name}
        <span className="sr-only">, click to rename</span>
      </button>
    );
  }

  // Unselected row: the click falls through to the card and selects it.
  if (!selected) {
    return (
      <span className={`block truncate ${className}`} title={name}>
        {name}
      </span>
    );
  }

  return (
    <button
      type="button"
      title={name}
      onClick={(e) => {
        e.stopPropagation();
        // The second click of a double-click that selected the card isn't a rename.
        if (e.detail > 1) return;
        startEditing();
      }}
      className={`block max-w-full cursor-text truncate text-left transition-colors hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${box} ${className}`}
    >
      {name}
      <span className="sr-only">, click to rename</span>
    </button>
  );
}
